import { useCallback, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';

import { getBuilderChatButtonClicked } from '../../../selectors/builderChat';
import { useApplicationContext } from '../../../utils/applicationContext';
import { releaseOnboarding, requestOnboarding } from '../../../utils/onboardingCoordinator';
import { getChatOnboardingCookie, setChatOnboardingCookie } from '../utils/chatOnboardingCookie';
import { TChatOnboardingType } from '../types';
import { ONBOARDING_COOKIE_NAME_MAP, CONFIG_MAP, CHAT_ONBOARDING_TEXT } from './constants';

interface IOnboardingTooltipResult {
  open: boolean;
  onboardingText: string;
  closeHandler: () => void;
}

export const useChatOnboarding = (onboardingType: TChatOnboardingType): IOnboardingTooltipResult => {
  const { config } = useApplicationContext();

  const [open, setOpen] = useState(false);

  const builderChatButtonClicked = useSelector(getBuilderChatButtonClicked);
  const onboardingText = config.get<string>(CHAT_ONBOARDING_TEXT[onboardingType]) ?? '';

  const cookieName = ONBOARDING_COOKIE_NAME_MAP[onboardingType];
  const onboardingId = `newbuilding-search-frontend.chat_onboarding.${onboardingType}`;

  const closeHandler = useCallback(() => {
    setOpen(false);
    releaseOnboarding(onboardingId);
  }, [onboardingId]);

  useEffect(() => {
    const isOnboardingEnabled = config.get<boolean>(CONFIG_MAP[onboardingType]);

    if (isOnboardingEnabled && !getChatOnboardingCookie(cookieName) && requestOnboarding(onboardingId)) {
      setOpen(true);
      setChatOnboardingCookie(cookieName);
    }
  }, [cookieName, config, onboardingId, onboardingType]);

  useEffect(() => () => releaseOnboarding(onboardingId), [onboardingId]);

  useEffect(() => {
    if (onboardingType === 'builder' && open && builderChatButtonClicked) {
      closeHandler();
    }
  }, [builderChatButtonClicked, open, onboardingType, closeHandler]);

  return {
    open,
    onboardingText,
    closeHandler,
  };
};
